import { CSSProperties } from "react";
import { Nullable } from "../interfaces/types";

const CTX_VAR_NAME = "__measureCtx";

/**
 * Get the text metrics by measuring the given input text in a canvas 2D context, using the given CSS font especifier.
 *
 * A valid use case for this is to check if a text fits in a given component width, by comparing the text width with the component width.
 *
 * The simplest use case is passing the size and the font family, like "12px Arial" as ctxFont value. Passing an empty string is not recommended,
 * since it will use the default font of the canvas context which might produce unexpected results. However, the ctxFont parameter can be as complex as needed,
 * so it can cover italic, bold, *rem, *em, etc; so the caller has full control.
 *
 * Internally the function reuses a single instance of the canvas context to reduce memory usage and to avoid CPU multiple calls to document.createElement function.
 * This way the function can be reused multiple times without creating extra overhead.
 *
 * The caller is responsible for handling the case when the function returns undefined, which can happen (if ever) when the canvas context is not available.
 *
 * @example
 * const componentWidth = 50;
 * const text = "Hello, World!";
 * const ctxFont = "12px Arial";
 * const metrics = getMetrics(text, ctxFont);
 * const textWidth = metrics.width ?? 0;
 * textWidth > componentWidth ? console.log("Text overflows") : console.log("Text fits");
 *
 * @param text The text to measure.
 * @param ctxFont The font to use for measuring the text.
 * @returns The text metrics object or undefined if the canvas rendering context is unavailable.
 * @see https://developer.mozilla.org/en-US/docs/Web/CSS/font
 * @see https://developer.mozilla.org/en-US/docs/Web/API/TextMetrics
 */
export function getMetrics(text: string, ctxFont: CSSProperties["font"]): Nullable<ReturnType<CanvasRenderingContext2D["measureText"]>> {
	if (!(CTX_VAR_NAME in window)) {
		Object(window)[CTX_VAR_NAME] = document.createElement("canvas").getContext("2d");
	}
	if (!Object(window)[CTX_VAR_NAME]) {
		return undefined;
	}
	Object(window)[CTX_VAR_NAME].font = ctxFont;
	return Object(window)[CTX_VAR_NAME].measureText(text);
}

/**
 * Helper function to simplify the process of measuring the width of a text targeting more common use cases. This is the preferred way to measure text width,
 * since it is way more targeted and type safer.
 * @param text The text to measure.
 * @param pxSize Font size in pixels.
 * @default 12
 * @param font The font to use for measuring the text.
 * It is type safe and only accepts the following values: "PTSansProNarrow", "Montserrat" or "PTSansPro".
 * @default "PTSansProNarrow"
 * @param fontWeight The font weight to use for measuring the text.
 * It accepts any valid CSS font weight value.
 * @default "normal"
 * @returns The actual measured width of the text in pixels or 0 if unable to compute the measure.
 */
export function measureTextWidth(
	text: string,
	pxSize = 12,
	font: "PTSansProNarrow" | "Montserrat" | "PTSansPro" = "PTSansProNarrow",
	fontWeight: CSSProperties["fontWeight"] = "normal"
): number {
	return getMetrics(text, `${fontWeight} ${pxSize}px ${font}`)?.width ?? 0;
}
